import { supabase } from './supabase';

// Compliance Requirements CRUD operations
export const complianceService = {
  // Load all compliance data for a project
  async loadProjectCompliance(projectId) {
    try {
      // Load requirements - order alphabetically by name
      const { data: requirements, error: reqError } = await supabase
        .from('compliance_requirements')
        .select('*')
        .eq('project_id', projectId)
        .order('name', { ascending: true });

      if (reqError) throw reqError;

      // Load wizard data
      const { data: wizardDataArray, error: wizError } = await supabase
        .from('compliance_wizard_data')
        .select('*')
        .eq('project_id', projectId);
      
      // Handle the case where no data exists (wizardDataArray will be empty array)
      const wizardData = wizardDataArray && wizardDataArray.length > 0 ? wizardDataArray[0] : null;

      // If wizardData exists but lacks new JSONB columns, provide defaults
      if (wizardData && !wizardData.collected_data) {
        wizardData.collected_data = {
          about: {
            siteName: wizardData.site_name,
            industry: wizardData.industry,
            location: wizardData.location,
            description: wizardData.project_description,
            specialRequirements: wizardData.special_requirements
          },
          workforce: {
            teamSize: wizardData.team_size
          },
          risk: {}
        };
      }
      if (wizardData && !wizardData.section_summaries) {
        wizardData.section_summaries = {
          about: '',
          workforce: '',
          risk: ''
        };
      }
      if (wizardData && !wizardData.expanded_context) {
        wizardData.expanded_context = {
          about: {},
          workforce: {},
          risk: {}
        };
      }

      // Load recommendations
      const { data: recommendations, error: recError } = await supabase
        .from('compliance_recommendations')
        .select('*')
        .eq('project_id', projectId);

      // Load risk assessment
      const { data: riskAssessment, error: riskError } = await supabase
        .from('compliance_risk_assessment')
        .select('*')
        .eq('project_id', projectId)
        .single();

      return {
        success: true,
        requirements: requirements || [],
        wizardData: wizardData || null,
        recommendations: recommendations || [],
        riskAssessment: riskAssessment || null
      };
    } catch (error) {
      console.error('Error loading compliance data:', error);
      return { success: false, error: error.message };
    }
  },

  // Save or update wizard data (individual columns)
  async saveWizardData(projectId, wizardData) {
    try {
      // Build update object with only provided fields that have actual values
      const dataToSave = {
        project_id: projectId,
        updated_date: new Date().toISOString()
      };

      // Add fields if they exist and are not null/undefined
      const source = wizardData.collected_data || wizardData;

      if (source.company_name !== undefined && source.company_name !== null) {
        dataToSave.company_name = source.company_name;
      }
      if (source.industry !== undefined && source.industry !== null) {
        dataToSave.industry = source.industry;
      }
      if (source.location !== undefined && source.location !== null) {
        dataToSave.location = source.location;
      }
      if (source.team_size !== undefined && source.team_size !== null) {
        dataToSave.team_size = source.team_size;
      }
      if (source.equipment !== undefined && source.equipment !== null) {
        dataToSave.equipment = source.equipment;
      }
      if (source.risks !== undefined && source.risks !== null) {
        dataToSave.risks = source.risks;
      }
      if (source.project_description !== undefined && source.project_description !== null) {
        dataToSave.project_description = source.project_description;
      }
      if (source.special_requirements !== undefined && source.special_requirements !== null) {
        dataToSave.special_requirements = source.special_requirements;
      }
      if (source.work_setting !== undefined && source.work_setting !== null) {
        dataToSave.work_setting = source.work_setting;
      }
      if (source.worker_roles !== undefined && source.worker_roles !== null) {
        dataToSave.worker_roles = source.worker_roles;
      }
      if (source.hazardous_substances !== undefined && source.hazardous_substances !== null) {
        dataToSave.hazardous_substances = source.hazardous_substances;
      }
      if (source.environment !== undefined && source.environment !== null) {
        dataToSave.environment = source.environment;
      }
      if (source.heights !== undefined && source.heights !== null) {
        dataToSave.heights = source.heights;
      }
      if (source.incident_history_file_id !== undefined && source.incident_history_file_id !== null) {
        dataToSave.incident_history_file_id = source.incident_history_file_id;
      }
      if (source.accreditations !== undefined && source.accreditations !== null) {
        dataToSave.accreditations = source.accreditations;
      }

      // Only update if we have fields to update beyond project_id and updated_date
      if (Object.keys(dataToSave).length <= 2) {
        console.log('No fields to update, skipping save');
        return { success: true, data: null };
      }

      const { data, error } = await supabase
        .from('compliance_wizard_data')
        .upsert(dataToSave, {
          onConflict: 'project_id'
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving wizard data:', error);
      return { success: false, error: error.message };
    }
  },

  // Save wizard state (individual columns)
  async saveWizardState(projectId, wizardState) {
    try {
      // Build update object with only provided fields that have actual values
      const dataToSave = {
        project_id: projectId,
        updated_date: new Date().toISOString()
      };

      // Add fields if they exist and are not null/undefined
      const source = wizardState.collected_data || wizardState.collectedData || wizardState;

      if (source.company_name !== undefined && source.company_name !== null) {
        dataToSave.company_name = source.company_name;
      }
      if (source.industry !== undefined && source.industry !== null) {
        dataToSave.industry = source.industry;
      }
      if (source.location !== undefined && source.location !== null) {
        dataToSave.location = source.location;
      }
      if (source.team_size !== undefined && source.team_size !== null) {
        dataToSave.team_size = source.team_size;
      }
      if (source.equipment !== undefined && source.equipment !== null) {
        dataToSave.equipment = source.equipment;
      }
      if (source.risks !== undefined && source.risks !== null) {
        dataToSave.risks = source.risks;
      }
      if (source.project_description !== undefined && source.project_description !== null) {
        dataToSave.project_description = source.project_description;
      }
      if (source.special_requirements !== undefined && source.special_requirements !== null) {
        dataToSave.special_requirements = source.special_requirements;
      }
      if (source.work_setting !== undefined && source.work_setting !== null) {
        dataToSave.work_setting = source.work_setting;
      }
      if (source.worker_roles !== undefined && source.worker_roles !== null) {
        dataToSave.worker_roles = source.worker_roles;
      }
      if (source.hazardous_substances !== undefined && source.hazardous_substances !== null) {
        dataToSave.hazardous_substances = source.hazardous_substances;
      }
      if (source.environment !== undefined && source.environment !== null) {
        dataToSave.environment = source.environment;
      }
      if (source.heights !== undefined && source.heights !== null) {
        dataToSave.heights = source.heights;
      }
      if (source.incident_history_file_id !== undefined && source.incident_history_file_id !== null) {
        dataToSave.incident_history_file_id = source.incident_history_file_id;
      }
      if (source.accreditations !== undefined && source.accreditations !== null) {
        dataToSave.accreditations = source.accreditations;
      }

      // Only update if we have fields to update beyond project_id and updated_date
      if (Object.keys(dataToSave).length <= 2) {
        console.log('No fields to update, skipping save');
        return { success: true, data: null };
      }

      const { data, error } = await supabase
        .from('compliance_wizard_data')
        .upsert(dataToSave, {
          onConflict: 'project_id'
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving wizard state:', error);
      return { success: false, error: error.message };
    }
  },

  // Add a single requirement
  async addRequirement(projectId, requirement) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      // Get user profile
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('auth_id', userId)
        .single();

      const { data, error } = await supabase
        .from('compliance_requirements')
        .insert({
          project_id: projectId,
          ...requirement,
          created_by: userProfile?.id,
          created_date: new Date().toISOString(),
          updated_date: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error adding requirement:', error);
      return { success: false, error: error.message };
    }
  },

  // Add multiple requirements
  async addMultipleRequirements(projectId, requirements) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;

      // Get user profile
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('auth_id', userId)
        .single();

      const requirementsWithMeta = requirements.map(req => ({
        project_id: projectId,
        ...req,
        created_by: userProfile?.id,
        created_date: new Date().toISOString(),
        updated_date: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('compliance_requirements')
        .insert(requirementsWithMeta)
        .select();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error adding multiple requirements:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a requirement
  async updateRequirement(requirementId, updates) {
    try {
      const { data, error } = await supabase
        .from('compliance_requirements')
        .update({
          ...updates,
          updated_date: new Date().toISOString()
        })
        .eq('id', requirementId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error updating requirement:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a requirement
  async deleteRequirement(requirementId) {
    try {
      const { error } = await supabase
        .from('compliance_requirements')
        .delete()
        .eq('id', requirementId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting requirement:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete all requirements for a project
  async deleteAllRequirements(projectId) {
    try {
      const { error } = await supabase
        .from('compliance_requirements')
        .delete()
        .eq('project_id', projectId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting all requirements:', error);
      return { success: false, error: error.message };
    }
  },

  // Save recommendations
  async saveRecommendations(projectId, recommendations) {
    try {
      // Delete existing recommendations
      await supabase
        .from('compliance_recommendations')
        .delete()
        .eq('project_id', projectId);

      // Format recommendations
      const formattedRecs = [];
      if (recommendations?.immediate) {
        recommendations.immediate.forEach(rec => {
          formattedRecs.push({
            project_id: projectId,
            type: 'immediate',
            recommendation: rec
          });
        });
      }
      if (recommendations?.shortTerm) {
        recommendations.shortTerm.forEach(rec => {
          formattedRecs.push({
            project_id: projectId,
            type: 'short_term',
            recommendation: rec
          });
        });
      }
      if (recommendations?.longTerm) {
        recommendations.longTerm.forEach(rec => {
          formattedRecs.push({
            project_id: projectId,
            type: 'long_term',
            recommendation: rec
          });
        });
      }

      if (formattedRecs.length > 0) {
        const { data, error } = await supabase
          .from('compliance_recommendations')
          .insert(formattedRecs)
          .select();

        if (error) throw error;
        return { success: true, data };
      }

      return { success: true, data: [] };
    } catch (error) {
      console.error('Error saving recommendations:', error);
      return { success: false, error: error.message };
    }
  },

  // Save risk assessment
  async saveRiskAssessment(projectId, riskAssessment) {
    try {
      const { data, error } = await supabase
        .from('compliance_risk_assessment')
        .upsert({
          project_id: projectId,
          risk_level: riskAssessment.level,
          risk_factors: riskAssessment.factors || [],
          mitigation_strategies: riskAssessment.mitigation || [],
          non_compliance_consequences: riskAssessment.nonComplianceConsequences || [],
          updated_date: new Date().toISOString()
        }, {
          onConflict: 'project_id'
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving risk assessment:', error);
      return { success: false, error: error.message };
    }
  },

  // Load chat messages
  async loadChatMessages(projectId) {
    try {
      const { data, error } = await supabase
        .from('compliance_chat_messages')
        .select('*')
        .eq('project_id', projectId)
        .order('created_date', { ascending: true });

      if (error) throw error;
      
      // Transform to the format expected by the component
      const messages = data.map(msg => ({
        role: msg.role,
        content: msg.content,
        metadata: msg.metadata || {}
      }));
      
      return { success: true, messages };
    } catch (error) {
      console.error('Error loading chat messages:', error);
      return { success: false, error: error.message, messages: [] };
    }
  },

  // Save a single chat message
  async saveChatMessage(projectId, message) {
    try {
      const { data, error } = await supabase
        .from('compliance_chat_messages')
        .insert({
          project_id: projectId,
          role: message.role,
          content: message.content,
          metadata: message.metadata || {},
          created_date: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving chat message:', error);
      return { success: false, error: error.message };
    }
  },

  // Save multiple chat messages
  async saveChatMessages(projectId, messages) {
    try {
      const messagesWithMeta = messages.map(msg => ({
        project_id: projectId,
        role: msg.role,
        content: msg.content,
        metadata: msg.metadata || {},
        created_date: new Date().toISOString()
      }));

      const { data, error } = await supabase
        .from('compliance_chat_messages')
        .insert(messagesWithMeta)
        .select();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error saving chat messages:', error);
      return { success: false, error: error.message };
    }
  },

  // Clear chat messages for a project
  async clearChatMessages(projectId) {
    try {
      const { error } = await supabase
        .from('compliance_chat_messages')
        .delete()
        .eq('project_id', projectId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error clearing chat messages:', error);
      return { success: false, error: error.message };
    }
  },

  // Save compliance assignments to team members
  async saveComplianceAssignments(projectId, assignments) {
    try {
      // Prepare assignments for insertion (tracking flag only)
      const assignmentsToInsert = [];

      for (const assignment of assignments) {
        for (const memberId of assignment.selectedMemberIds) {
          assignmentsToInsert.push({
            project_id: projectId,
            requirement_id: assignment.requirement.id,
            team_member_id: memberId,
            assigned: true
          });
        }
      }

      // Insert assignments (upsert to handle duplicates)
      const { data, error } = await supabase
        .from('compliance_assignments')
        .upsert(assignmentsToInsert, {
          onConflict: 'requirement_id,team_member_id,project_id'
        })
        .select();

      if (error) throw error;

      return { success: true, data, count: assignmentsToInsert.length };
    } catch (error) {
      console.error('Error saving compliance assignments:', error);
      return { success: false, error: error.message };
    }
  },

  // Load compliance assignments for a project
  async loadComplianceAssignments(projectId) {
    try {
      const { data, error } = await supabase
        .from('compliance_assignments')
        .select(`
          *,
          requirement:compliance_requirements(*),
          team_member:user_profiles!team_member_id(*)
        `)
        .eq('project_id', projectId)
        .order('created_date', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error loading compliance assignments:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
};

export default complianceService;